import {
  Observable,
  BehaviorSubject,
  finalize,
  take,
  firstValueFrom,
} from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar.service';

export interface ApiOperationConfig<T> {
  operation: () => Observable<T>;
  onSuccess: (result: T) => void;
  onError?: (error: any) => void;
  errorMessage: string;
  loadingState?: BehaviorSubject<boolean>;
}

export interface AsyncApiOperationConfig<T>
  extends Omit<ApiOperationConfig<T>, 'operation'> {
  operation: () => Observable<T>;
}

export class ApiOperationHelper {
  static executeOperation<T>(
    config: ApiOperationConfig<T>,
    snackbarService: SnackbarService
  ): void {
    if (config.loadingState) {
      config.loadingState.next(true);
    }

    config
      .operation()
      .pipe(take(1))
      .pipe(
        finalize(() => {
          if (config.loadingState) {
            config.loadingState.next(false);
          }
        })
      )
      .subscribe({
        next: (result) => {
          config.onSuccess(result);
        },
        error: (error) => {
          const detailedMessage = this.getDetailedErrorMessage(
            error,
            config.errorMessage
          );
          snackbarService.error(detailedMessage);
          config.onError?.(error);
        },
      });
  }

  static async executeAsyncOperation<T>(
    config: AsyncApiOperationConfig<T>,
    snackbarService: SnackbarService
  ): Promise<T | null> {
    let result: T | null = null;

    if (config.loadingState) {
      config.loadingState.next(true);
    }

    try {
      result = await firstValueFrom(config.operation().pipe(take(1)));
      config.onSuccess(result);
    } catch (error) {
      const detailedMessage = this.getDetailedErrorMessage(
        error,
        config.errorMessage
      );
      snackbarService.error(detailedMessage);
      config.onError?.(error);
    } finally {
      if (config.loadingState) {
        config.loadingState.next(false);
      }
    }

    return result;
  }

  private static getDetailedErrorMessage(
    error: any,
    baseMessage: string
  ): string {
    const errorDetail = error?.error?.message ? ': ' + error.error.message : '';
    return baseMessage + errorDetail;
  }

  static executeWithStateRollback<T>(
    config: ApiOperationConfig<T> & { rollbackAction: () => void },
    snackbarService: SnackbarService
  ): void {
    this.executeOperation(
      {
        ...config,
        onError: (error) => {
          config.rollbackAction();
          config.onError?.(error);
        },
      },
      snackbarService
    );
  }
}
